import { useRef, useEffect, useState } from 'react';
import Velcro from 'utils/velcro/velcro';
import { toParams, toSearch } from 'utils/history';
import history from 'store/history/history';
import { useLocation } from 'react-router-dom';

import { getDiff, formatVariables, flatFilters } from 'components/utils/search';

export default function useVelcro({
  getParamsToBeHidden = () => null,
  parseInitialParams = (params) => params,
  rootKeys = ['skip', 'limit', 'sort'],
  schema,
  handlers,
  search = '',
  ignoreUpdates = false,
  allowSkipInUrl = false,
}) {
  const velcro = useRef(null);
  const mountRef = useRef(false);
  const searchStringRef = useRef();
  const initialState = { initial: null, default: null };
  const location = useLocation();

  if (!velcro.current) {
    velcro.current = new Velcro(schema, handlers);

    if (!search) {
      const { value: defaults } = velcro.current.validate({});
      initialState.defaultValues = formatVariables(defaults, rootKeys);
      initialState.initialValues = initialState.defaultValues;
    }
  }
  const [state, setState] = useState(initialState);

  useEffect(() => {
    if (ignoreUpdates && mountRef.current) {
      return;
    }

    mountRef.current = true;

    const params = toParams(search);

    async function loadPre() {
      velcro.current = new Velcro(schema, handlers);
      // converts strings to arrays
      const velcroParsedState = velcro.current.parse(params);
      // will convert string to ints

      const { value: initials } = velcro.current.validate(velcroParsedState, {
        convert: true,
        abortEarly: false,
        stripUnknown: true,
      });

      const { value: defaults } = velcro.current.validate({});

      // Can transform the initials values.
      // Eg: with a campaignId, it can insert the categoryIds and demgoraphics filters.
      const parsedInitials = await parseInitialParams(
        initials,
        defaults,
        getDiff(defaults, initials),
      );

      const initialValues = formatVariables(parsedInitials, rootKeys);
      const defaultValues = formatVariables(defaults, rootKeys);

      setState({
        initialValues,
        defaultValues,
      });
    }

    if (search !== searchStringRef.current) {
      searchStringRef.current = search;
      loadPre();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  function toSearchString(values, hiddenValues) {
    const flatValues = flatFilters(values, schema);
    const { value: hiddenFlatValues, error } = velcro.current.validate(
      hiddenValues || {},
    );

    if (error) {
      throw new Error(error);
    }

    const convertedValues = velcro.current.convert(flatValues);
    const convertedHiddenValues = velcro.current.convert(hiddenFlatValues);

    const diff = getDiff(convertedHiddenValues, convertedValues);
    if (!allowSkipInUrl) {
      delete diff.skip;
    }

    return toSearch(diff);
  }

  async function updateUrl(values, shouldReplace) {
    // the values to omit from the url
    const hiddenValues = await getParamsToBeHidden(values);
    const searchString = toSearchString(values, hiddenValues);

    if (searchString !== search) {
      searchStringRef.current = searchString;
      const currentState = history.location.state || {};

      // Preserve the current pathname and only update the search params
      const to = {
        pathname: location.pathname, // Use current pathname
        search: searchString,
        state: {
          ...currentState,
          keepReferrer: true, // Preserve navigation state
          from: location.pathname,
          previousPath: location.pathname,
        },
      };

      if (shouldReplace) {
        history.replace(to);
      } else {
        history.push(to);
      }
    }
  }

  return {
    velcro: velcro.current,
    defaultValues: state.defaultValues,
    initialValues: state.initialValues,
    updateUrl,
    toSearchString,
  };
}
